import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
  alpha,
  Card,
  CardContent,
  Fade,
  styled,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

// Componente de ícone estilizado
const FeatureIcon = styled(Box)(
  ({ theme, color = theme.palette.primary.main }) => ({
    width: 64,
    height: 64,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(
        String(color),
        0.1
      )} 0%, ${alpha(String(color), 0.05)} 100%)`,
      borderRadius: "inherit",
    },
    "& svg": {
      fontSize: 32,
      color,
      position: "relative",
      zIndex: 1,
    },
  })
);

// Array de características com cores hex definidas
const features = [
  {
    icon: <LocalHospitalIcon />,
    title: "Cobertura Completa",
    description:
      "Tenha acesso a uma ampla rede de hospitais, clínicas e laboratórios de alta qualidade para cuidar da sua saúde.",
    delay: 200,
    color: "#673AB7",
  },
  {
    icon: <AttachMoneyIcon />,
    title: "Economia Garantida",
    description:
      "Economize até 50% em seu plano atual sem perder benefícios, com preços exclusivos e vantajosos.",
    delay: 400,
    color: "#FF5252",
  },
  {
    icon: <PeopleIcon />,
    title: "Planos Familiares",
    description:
      "Planos especiais para famílias com descontos progressivos para cada dependente adicionado.",
    delay: 600,
    color: "#2196F3",
  },
  {
    icon: <VerifiedUserIcon />,
    title: "Planos Regulamentados",
    description:
      "Todos os planos são regulamentados pela ANS, garantindo segurança e confiabilidade no seu investimento.",
    delay: 800,
    color: "#4CAF50",
  },
  {
    icon: <SupportAgentIcon />,
    title: "Suporte Exclusivo",
    description:
      "Atendimento humanizado e personalizado para solucionar suas dúvidas e necessidades a qualquer momento.",
    delay: 1000,
    color: "#FF9800",
  },
  {
    icon: <HealthAndSafetyIcon />,
    title: "Medicina Preventiva",
    description:
      "Programas de prevenção e promoção à saúde para garantir seu bem-estar e qualidade de vida.",
    delay: 1200,
    color: "#F44336",
  },
];

export const FeaturesSection = () => {
  const theme = useTheme();

  return (
    <Box
      id="benefícios"
      sx={{
        py: { xs: 8, md: 12 },
        background: "#FFFFFF",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Elementos decorativos */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: -150, md: -200 },
          right: { xs: -100, md: -150 },
          width: { xs: 300, md: 400 },
          height: { xs: 300, md: 400 },
          borderRadius: "50%",
          background: alpha(theme.palette.primary.light, 0.05),
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: { xs: -150, md: -200 },
          left: { xs: -100, md: -150 },
          width: { xs: 300, md: 400 },
          height: { xs: 300, md: 400 },
          borderRadius: "50%",
          background: alpha(theme.palette.secondary.light, 0.05),
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontWeight: 600,
              color: theme.palette.secondary.main,
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Vantagens Exclusivas
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
              color: theme.palette.text.primary,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Por que escolher nossos planos de saúde?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
              mb: 2,
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
            }}
          >
            Oferecemos uma experiência completa em planos de saúde com
            atendimento personalizado, as melhores operadoras do mercado e
            condições exclusivas para você.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in={true} timeout={feature.delay}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    transition: "all 0.3s ease",
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: `0 20px 30px ${alpha(
                        theme.palette.common.black,
                        0.08
                      )}`,
                      borderColor: alpha(feature.color, 0.3),
                      "& .feature-icon": {
                        transform: "scale(1.1)",
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <FeatureIcon
                      className="feature-icon"
                      color={feature.color}
                      sx={{
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {feature.icon}
                    </FeatureIcon>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Texto adicional após as features */}
        <Box
          sx={{
            mt: 6,
            pt: 6,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            component="p"
            sx={{
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
              maxWidth: "900px",
              mx: "auto",
            }}
          >
            {`"Nosso compromisso é oferecer o plano ideal para sua necessidade,
            com o melhor custo-benefício do mercado."`}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
            }}
          >
            Mais de 10.000 clientes satisfeitos em todo o Brasil já escolheram
            nossos serviços e economizam mensalmente em seus planos de saúde.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
